import { type FC } from 'react';

import { Text } from '../Typography';
import {
  autoRootStyles,
  compactItemStyles,
  compactListStyles,
  compactRootStyles,
  horizontalItemStyles,
  horizontalListStyles,
  labelCurrentStyles,
  labelStyles,
  markerCurrentStyles,
  markerStyles,
  progressStyles,
  rootStyles,
  stepStyles,
  visuallyHiddenStyles,
} from './Stepper.styles';
import {
  type StepperProps,
  type StepperStep,
  type StepperVariant,
} from './Stepper.types';

type StepState = 'completed' | 'current' | 'upcoming';

const resolveCurrentIndex = (
  steps: StepperStep[],
  currentStep: number | string
): number => {
  if (steps.length === 0) return -1;

  if (typeof currentStep === 'number') {
    if (!Number.isFinite(currentStep)) return -1;
    const index = Math.trunc(currentStep);
    return Math.min(Math.max(index, 0), steps.length - 1);
  }

  return steps.findIndex((step) => step.id === currentStep);
};

const getStepState = (index: number, currentIndex: number): StepState => {
  if (index === currentIndex) return 'current';
  if (currentIndex >= 0 && index < currentIndex) return 'completed';
  return 'upcoming';
};

const Stepper: FC<StepperProps> = ({
  steps,
  currentStep,
  variant = 'auto',
  ariaLabel = 'Progress',
  className,
}) => {
  const currentIndex = resolveCurrentIndex(steps, currentStep);
  const current = currentIndex >= 0 ? steps[currentIndex] : undefined;
  const showHorizontal = variant === 'horizontal' || variant === 'auto';
  const showCompact = variant === 'compact' || variant === 'auto';

  const renderSteps = (layout: Exclude<StepperVariant, 'auto'>) =>
    steps.map((step, index) => {
      const state = getStepState(index, currentIndex);
      const isCurrent = state === 'current';

      return (
        // list-style: none drops implicit listitem semantics in some browsers.
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <li
          key={step.id}
          aria-current={isCurrent ? 'step' : undefined}
          aria-label={`${index + 1}. ${step.label}`}
          css={
            layout === 'horizontal' ? horizontalItemStyles : compactItemStyles
          }
          data-state={state}
          role="listitem"
        >
          {layout === 'horizontal' ? (
            <span css={stepStyles}>
              <span
                css={[markerStyles, isCurrent && markerCurrentStyles]}
                data-part="marker"
              >
                {index + 1}
              </span>
              <Text
                as="span"
                css={[labelStyles, isCurrent && labelCurrentStyles]}
                fontWeight={isCurrent ? 'bold' : 'normal'}
                size="sm"
              >
                {step.label}
              </Text>
            </span>
          ) : (
            <span css={visuallyHiddenStyles}>
              {index + 1}. {step.label}
            </span>
          )}
        </li>
      );
    });

  return (
    <nav
      aria-label={ariaLabel}
      className={className}
      css={[rootStyles, variant === 'auto' && autoRootStyles]}
      data-variant={variant}
    >
      {showHorizontal && (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ol css={horizontalListStyles} data-layout="horizontal" role="list">
          {renderSteps('horizontal')}
        </ol>
      )}
      {showCompact && (
        <div css={compactRootStyles} data-layout="compact">
          {current && (
            <Text
              as="p"
              css={progressStyles}
              fontWeight="semibold"
              size="xs"
              uppercase
            >
              {`Step ${currentIndex + 1} of ${steps.length} · ${current.label}`}
            </Text>
          )}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ol css={compactListStyles} role="list">
            {renderSteps('compact')}
          </ol>
        </div>
      )}
    </nav>
  );
};

Stepper.displayName = 'Stepper';

export default Stepper;
